import React, { useState, useEffect, useRef } from "react";
import { LayoutGrid, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Floating category nav for the customer menu page.
 * Shows a FAB at bottom-right → opens a bottom sheet with all categories.
 * Tapping a category scrolls to #menu and switches the active tab.
 */
export default function FloatingMenuNav({ tabs, menu, activeTab, onSelectTab }) {
  const [open, setOpen] = useState(false);

  const select = (id) => {
    onSelectTab(id);
    setOpen(false);
    const el = document.getElementById("menu");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Count items per category
  const countByTab = {};
  for (const t of tabs) {
    countByTab[t.id] = menu.filter((i) => i.category === t.id && i.available !== false).length;
  }

  if (!tabs.length) return null;

  return (
    <>
      {/* FAB */}
      <button
        onClick={() => setOpen(true)}
        data-testid="floating-menu-fab"
        aria-label="Browse menu categories"
        className="fixed bottom-[204px] right-4 sm:right-5 z-40 w-14 h-14 rounded-full bg-biga-ink text-biga-cream shadow-xl flex items-center justify-center hover:bg-biga-orange transition-colors sm:bottom-24"
      >
        <LayoutGrid size={22} />
      </button>

      {/* Bottom sheet */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-biga-ink/40 backdrop-blur-sm"
            />
            <motion.div
              key="sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[60] bg-biga-cream rounded-t-3xl border-t-2 border-biga-ink max-h-[70vh] overflow-y-auto"
              data-testid="floating-menu-sheet"
            >
              <div className="max-w-lg mx-auto">
              <div className="flex items-center justify-between px-5 pt-5 pb-3 sticky top-0 bg-biga-cream z-10">
                <h3 className="font-display font-black text-2xl text-biga-ink">Menu</h3>
                <button onClick={() => setOpen(false)} data-testid="floating-menu-close" className="w-9 h-9 rounded-full bg-biga-ink/5 flex items-center justify-center hover:bg-biga-ink/15">
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 pb-6 space-y-1">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => select(t.id)}
                    data-testid={`floating-cat-${t.id}`}
                    className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-left transition-colors ${
                      activeTab === t.id
                        ? "bg-biga-orange text-biga-cream"
                        : "hover:bg-biga-ink/5 text-biga-ink"
                    }`}
                  >
                    <span className="font-bold text-sm">{t.label}</span>
                    <span className={`text-xs font-bold ${activeTab === t.id ? "text-biga-cream/70" : "text-biga-ink/40"}`}>
                      {countByTab[t.id] || 0} {(countByTab[t.id] || 0) === 1 ? "item" : "items"}
                    </span>
                  </button>
                ))}
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
